import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  GeodataImage,
  PatchGeodataImagesParameters,
  PatchGeodataImagesResponse,
} from '../../../../../../typings/api/skymap/rest/v0/geodata';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { useErrorHandling } from '../../../hooks/use-error-handling';

export const usePatchGeodataImagesMutation = (geodataId: string, photoCount: number) => {
  const { clearErrors: clearApiErrors, handleError, buildErrorList } = useErrorHandling();
  const queryClient = useQueryClient();
  const errorList = useMemo(() => buildErrorList(), [buildErrorList]);

  const mutation = useMutation({
    mutationFn: (params: PatchGeodataImagesParameters) => {
      console.log('mutationFn called with params:', params);
      const service = SkyMapAxiosServiceFactory.instance.createGeodataServiceV0();
      console.log('Service created:', service);
      console.log('Service has patchGeodataImages:', typeof service.patchGeodataImages);
      const result = service.patchGeodataImages(params);
      console.log('patchGeodataImages called, result:', result);
      return result;
    },
    retry: false,
    networkMode: 'always',
    onSuccess: (response: PatchGeodataImagesResponse) => {
      // Update the query cache with the patched images
      queryClient.setQueryData<GeodataImage[] | undefined>(
        ['getGeodataImages', geodataId, photoCount],
        (oldData) => {
          if (!oldData) {
            return oldData;
          }

          // Filter out error items and only keep successful GeodataImage updates
          const updatedImages = response.filter(
            (item): item is GeodataImage => 'id' in item && !('status' in item),
          );

          if (updatedImages.length === 0) {
            return oldData;
          }

          // Create a map of updated images by id
          const updatedById = new Map(updatedImages.map((img: GeodataImage) => [img.id, img]));

          // Update existing images or add new ones
          const result = oldData.map((existingImage) => {
            const updated = updatedById.get(existingImage.id);
            return updated ? { ...existingImage, ...updated } : existingImage;
          });

          // Add any new images that weren't in the old data
          updatedImages.forEach((updatedImage: GeodataImage) => {
            if (!oldData.some((img) => img.id === updatedImage.id)) {
              result.push(updatedImage);
            }
          });

          return result;
        },
      );
    },
    onMutate: () => clearApiErrors(),
    onError: (err) => handleError(err),
  });

  return {
    patchGeodataImages: (
      params: PatchGeodataImagesParameters,
      options?: UseMutationOptions<PatchGeodataImagesResponse, Error, PatchGeodataImagesParameters>,
    ) => {
      mutation.mutate(params, options);
    },
    patchGeodataImagesAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    errorList,
  };
};
